import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { SortResults } from '@opensearch-project/opensearch/api/_types/_common';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { ResponseBody } from '@opensearch-project/opensearch/api/_types/_core.search';
import cloneDeep from 'lodash/cloneDeep';

import { EnumCustomerType } from '@/types';
import { ResolvedPrioritization } from '@/utils';
import {
  InitialProductQueryFilters,
  PRODUCT_FILTER_ATTRIBUTES,
} from '@/constants';

import {
  buildFacetQueries,
  buildQueryFilterFromSearchParams,
  buildSearchQueryFilter,
  buildSmartFacetAggregations,
  buildSortFilterFromSearchParams,
  getPriceFilter,
} from './facets';
import { CollapseParam, DefaultSortingFilter } from '../constants';
import { mergeListingSortWithPrioritization } from './prioritizationSort';

/** Resolve bool.must for facet listing query (root bool or inside function_score.query). */
export const getListingBoolMustArray = (
  query: QueryContainer | undefined,
): QueryContainer[] | undefined => {
  if (!query || typeof query !== 'object') {
    return undefined;
  }
  const q = query as Record<string, unknown>;
  if (q.bool && typeof q.bool === 'object') {
    const must = (q.bool as { must?: QueryContainer[] }).must;
    if (Array.isArray(must)) {
      return must;
    }
  }
  const fs = q.function_score as { query?: QueryContainer } | undefined;
  if (fs?.query) {
    return getListingBoolMustArray(fs.query);
  }
  return undefined;
};

type FacetAggWithFilter = {
  filter?: { bool?: { must?: QueryContainer[] } };
};

const appendClauseToFacetAggs = (
  aggs: Record<string, FacetAggWithFilter>,
  clause: QueryContainer,
): Record<string, FacetAggWithFilter> =>
  Object.fromEntries(
    Object.entries(aggs).map(([key, value]) => {
      if (!value.filter?.bool?.must) {
        return [key, value];
      }

      return [
        key,
        {
          ...value,
          filter: {
            bool: {
              must: [...value.filter.bool.must, clause],
            },
          },
        },
      ];
    }),
  );

/** Appends runtime filters to listing query + facet agg filters. Clones once. */
const appendQueryClausesToSearchBody = (
  searchBody: Search_RequestBody,
  clauses: QueryContainer[],
): Search_RequestBody => {
  if (!clauses.length) {
    return searchBody;
  }

  const body = cloneDeep(searchBody);

  for (const clause of clauses) {
    const must = getListingBoolMustArray(body.query);
    if (must) {
      must.push(clause);
    }

    if (body.aggs?.facets?.aggs) {
      body.aggs.facets.aggs = appendClauseToFacetAggs(
        body.aggs.facets.aggs as Record<string, FacetAggWithFilter>,
        clause,
      ) as typeof body.aggs.facets.aggs;
    }
  }

  return body;
};

const getCustomerTypes = (
  customerType?: EnumCustomerType,
  isStrictMedicalEnabled?: boolean,
): EnumCustomerType[] => [
  EnumCustomerType.BOTH,
  ...(customerType ? [customerType] : []),
  ...(customerType === EnumCustomerType.MEDICAL && !isStrictMedicalEnabled
    ? [EnumCustomerType.ADULT]
    : []),
];

const buildCustomerTypeFilter = (
  customerType: EnumCustomerType,
  isStrictMedicalEnabled?: boolean,
): QueryContainer => ({
  bool: {
    should: getCustomerTypes(customerType, isStrictMedicalEnabled).map(
      type => ({
        match: {
          customCustomerType: type,
        },
      }),
    ),
  },
});

const injectCustomerTypeToSearchBody = ({
  searchBody,
  customerType,
  isStrictMedicalEnabled,
}: {
  searchBody: Search_RequestBody;
  customerType?: EnumCustomerType;
  isStrictMedicalEnabled?: boolean;
}) => {
  if (!customerType || customerType === EnumCustomerType.ALL) {
    return searchBody;
  }

  return appendQueryClausesToSearchBody(searchBody, [
    buildCustomerTypeFilter(customerType, isStrictMedicalEnabled),
  ]);
};

/** CSV from express_menu_config.inventory_provider_ids (Treez locationId filter). */
const parseInventoryLocationIds = (
  value: string | null | undefined,
): string[] => {
  if (!value?.trim()) {
    return [];
  }

  return value
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
};

/**
 * Express menu: product must have stock at least one configured location.
 * Index field `customInventoryIds` mirrors productData.inventory[].locationId (verified vs Treez API).
 */
const buildInventoryLocationFilter = (
  inventoryLocationIds: string[],
): QueryContainer => ({
  terms: {
    customInventoryIds: inventoryLocationIds,
  },
});

const buildPriceFilterFromQueryRecord = (
  queryFiltersByFacets: Record<string, QueryContainer>,
  includePromoItems: boolean,
): Record<string, QueryContainer> => {
  const priceFilterStartValue = (
    queryFiltersByFacets[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.bool
      ?.filter as QueryContainer
  )?.range?.[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.gte;
  const priceFilterEndValue = (
    queryFiltersByFacets[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.bool
      ?.filter as QueryContainer
  )?.range?.[PRODUCT_FILTER_ATTRIBUTES._PRICE]?.lte;

  const priceQuery = getPriceFilter({
    includePromoItems,
    priceStart: priceFilterStartValue
      ? Number(priceFilterStartValue)
      : undefined,
    priceEnd: priceFilterEndValue ? Number(priceFilterEndValue) : undefined,
  });
  return {
    [PRODUCT_FILTER_ATTRIBUTES._PRICE]: priceQuery,
  };
};

const buildSearchBodyFromParams = ({
  searchParams = {},
  filters = {},
  limit = 20,
  includePromoItems = false,
  prioritization,
  prismicDefaultSort,
  omitAggs,
}: {
  searchParams?: Record<string, string | string[] | boolean[]>;
  filters?: Record<string, string | string[] | boolean[]>;
  limit?: number;
  includePromoItems?: boolean;
  prioritization?: ResolvedPrioritization;
  /** Prismic-configured sort string when URL has no sort (matches OpenSearchProvider defaultSort). */
  prismicDefaultSort?: string;
  omitAggs?: boolean;
}) => {
  const sortFilter = buildSortFilterFromSearchParams(searchParams);
  const defaultSortFilter = prismicDefaultSort
    ? buildSortFilterFromSearchParams({ sort: prismicDefaultSort })
    : undefined;
  const hasSearchText = Boolean(
    String((searchParams.query as string | undefined) ?? '').trim().length,
  );
  const mergedSort = mergeListingSortWithPrioritization({
    prioritization,
    effectiveSort: sortFilter,
    listingDefaultSort: defaultSortFilter,
    hasSearchText,
  });

  const searchFilter = buildSearchQueryFilter(
    (searchParams.query || '') as string,
  );
  const page = Math.max(parseInt((searchParams.page || '1') as string), 1);
  const from = (page - 1) * limit;

  const queryFilters = buildQueryFilterFromSearchParams({
    ...InitialProductQueryFilters,
    ...searchParams,
    ...filters,
  });

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFilters,
    includePromoItems,
  );

  const searchBody: Search_RequestBody = {
    from,
    size: limit,
    query: buildFacetQueries({
      ...queryFilters,
      ...searchFilter,
      ...priceFilter,
    }),
    ...(omitAggs
      ? {}
      : {
          aggs: buildSmartFacetAggregations({
            ...queryFilters,
            ...searchFilter,
            ...priceFilter,
          }),
        }),
    ...CollapseParam,
    ...(mergedSort ? { sort: mergedSort } : {}),
  };

  return { searchBody };
};

const buildSearchBodyFromParamsWithSearchAfter = ({
  searchParams = {},
  filters = {},
  limit = 20,
  includePromoItems = false,
  omitAggs,
  searchAfter,
  omitCollapse = false,
}: {
  searchParams?: Record<string, string | string[] | boolean[]>;
  filters?: Record<string, string | string[] | boolean[]>;
  limit?: number;
  includePromoItems?: boolean;
  omitAggs?: boolean;
  searchAfter?: SortResults;
  omitCollapse?: boolean;
}) => {
  const searchFilter = buildSearchQueryFilter(
    (searchParams.query || '') as string,
  );

  const queryFilters = buildQueryFilterFromSearchParams({
    ...InitialProductQueryFilters,
    ...searchParams,
    ...filters,
  });

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFilters,
    includePromoItems,
  );

  const searchBody: Search_RequestBody = {
    size: limit,
    query: buildFacetQueries({
      ...queryFilters,
      ...searchFilter,
      ...priceFilter,
    }),
    ...(omitAggs
      ? {}
      : {
          aggs: buildSmartFacetAggregations({
            ...queryFilters,
            ...searchFilter,
            ...priceFilter,
          }),
        }),
    ...(omitCollapse ? {} : CollapseParam),
    sort: DefaultSortingFilter,
    ...(searchAfter ? { search_after: searchAfter } : {}),
  };

  return { searchBody };
};

const getSearchAfterFromOpenSearchResponse = (
  result: ResponseBody,
): SortResults | undefined => {
  const hits = result?.hits?.hits ?? [];

  if (!hits.length) return undefined;

  return hits[hits.length - 1]?.sort;
};

export {
  appendQueryClausesToSearchBody,
  buildCustomerTypeFilter,
  injectCustomerTypeToSearchBody,
  parseInventoryLocationIds,
  buildInventoryLocationFilter,
  buildSearchBodyFromParams,
  buildSearchBodyFromParamsWithSearchAfter,
  getSearchAfterFromOpenSearchResponse,
  buildPriceFilterFromQueryRecord,
};
